import React, { useCallback, useState } from 'react'

import { useDrop, useDrag } from '@sketch/modules-common'

import { useToast } from '@sketch/toasts'

import {
  StorageItemIdentity,
  useTransferStorageItems,
} from '../../storageItems'
import { ErrorHandler } from '@sketch/tracing'
import { MovedToProjectToast } from '../../storageItems/components'
import { useGetDocumentListSettingsQuery } from '@sketch/gql-types'
import { createDnDGhost } from '../../storageItems/utils/createDnDGhost'
import { useSelectionContext } from '../../storageItems/components/SelectionContext'

interface UseNestedProjectDropSharesProps {
  projectIdentifier: string
  workspaceId: string
  disabled?: boolean
}

/**
 * Creates a droppable area for nested projects, accepts dragging both shares
 * or projects.
 */
export const useNestedProjectDropShares = ({
  projectIdentifier,
  workspaceId,
  disabled = false,
}: UseNestedProjectDropSharesProps) => {
  const { showToast } = useToast()
  const [isDroppableAreaEnabled, setIsDroppableAreaEnabled] = useState(false)

  const [transferItemsRaw] = useTransferStorageItems({
    onError: errorMessage => {
      showToast(errorMessage, 'negative')
    },
  })

  const { selectedItems } = useSelectionContext()
  const { data } = useGetDocumentListSettingsQuery()

  const transferItems = useCallback(
    ({
      items,
      projectId,
      oldProjectId,
    }: {
      items: StorageItemIdentity[]
      projectId: string
      oldProjectId: string | undefined
    }) => {
      transferItemsRaw({
        onCompleted: data => {
          const { storageItems } = data.transferStorageItems
          const [storageItem] = storageItems

          const newProject =
            storageItem.__typename === 'Share'
              ? storageItem.project
              : storageItem.parentProjects.find(
                  x => x.identifier === storageItem.parentProjectIdentifier
                )

          if (!newProject) {
            ErrorHandler.shouldNeverHappen('newProject is not defined')
            return
          }

          showToast(
            <MovedToProjectToast
              project={newProject}
              type={storageItem.__typename === 'Share' ? 'Document' : 'Folder'}
              workspaceId={workspaceId}
            />
          )
        },
        items,
        location: {
          source: { workspaceId, projectId: oldProjectId },
          destination: { workspaceId, projectId },
        },
      })
    },
    [showToast, transferItemsRaw, workspaceId]
  )

  const dragEvents = useDrag({
    item: { __typename: 'Project', identifier: projectIdentifier },
    relatedItems: selectedItems,
    customDragImage: true,
    createCustomElement: el =>
      createDnDGhost(el, data?.documentsLayout === 'LIST' ? 'list' : 'grid'),
  })

  const dropEvents = useDrop({
    onStorageItemsDragOver: (_, relatableItems) => {
      if (
        relatableItems.find(value => value.identifier === projectIdentifier)
      ) {
        return
      }

      setIsDroppableAreaEnabled(true)
    },
    onStorageItemsDropped: (draggedItem, relatableItems) => {
      if (
        relatableItems.find(value => value.identifier === projectIdentifier)
      ) {
        return
      }

      transferItems({
        items: relatableItems,
        projectId: projectIdentifier,
        oldProjectId:
          draggedItem.__typename === 'Share'
            ? draggedItem.project?.parentProjectIdentifier || undefined
            : (draggedItem?.parentProjectIdentifier ?? undefined),
      })
    },

    onUnknownDragOver: () => {
      setIsDroppableAreaEnabled(false)
    },
  })

  return {
    draggable: !disabled,
    isDroppableAreaEnabled,
    ...dragEvents,
    ...dropEvents,
  }
}
